// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar, type OptionsObject } from 'notistack';
import { useConfirmPopup } from '@shared/ui-kit/components/popups';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import {
  CLIENTS_PAGE_SIZE,
  useClients,
  type ClientRow,
} from '@shared/advisory/aggregation/hooks/use-clients';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { buildCreateClientBody } from '@shared/advisory/aggregation/utils/build-create-client-body';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useDebounce } from '@shared/ui-kit/hooks';
import { type KpiBoxesProps } from '@shared/ui-kit/components';
import { formatToMoney } from '@shared/ui-kit/utils/format-to-money';
import type * as BrokerageTypes from '@shared/data-access/types/brokerage-types';
import type { PaginationState } from '@tanstack/react-table';

const commonSnackProps: OptionsObject = {
  variant: 'success',
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

interface ExpectedResultValue {
  data?: BrokerageTypes.Client;
  error?: {
    status: number | string;
    data?: { detail: string };
    error?: string;
  };
}

export interface UseListClientsOptions {
  onSetClientProfile?: (client: ClientRow, shouldNavigate?: boolean) => void;
  onViewProfile?: (client: ClientRow) => void;
  onSelectClient?: (client: ClientRow) => void;
}

export const useListClients = (options?: UseListClientsOptions) => {
  const { t } = useTranslation();
  const showConfirm = useConfirmPopup();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [textSearch, setTextSearch] = useState('');
  const debouncedSearch = useDebounce(textSearch, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const {
    clientsData,
    clientsPage,
    clientsQuery,
    status: clientsStatus,
    hasData: clientsHasData,
    onRefresh: onRefreshClients,
    userId,
  } = useClients({ page, search: debouncedSearch });

  const isFetchingClients = clientsQuery.isFetching;
  const isLoadingListClients = clientsQuery.isLoading;

  const currency = useSelector(selectAccountCurrency);

  const isLoading = useMemo(
    () => [isFetchingClients, isLoadingListClients].some(Boolean),
    [isFetchingClients, isLoadingListClients],
  );

  const kpis = useMemo<KpiBoxesProps[]>(
    () => [
      {
        text: t('accounts.total-clients', { ns: 'components' }),
        value: clientsPage?.total_clients ?? 0,
        dataTestid: 'kpi-total-clients',
        icon: 'Users',
        color: 'main',
      },
      {
        text: t('accounts.active-clients', { ns: 'components' }),
        value: clientsPage?.active_clients ?? 0,
        dataTestid: 'kpi-active-clients',
        icon: 'UserCheck',
        color: 'success',
      },
      {
        text: t('accounts.assets-managed', { ns: 'components' }),
        value: formatToMoney(clientsPage?.total_amount ?? 0, currency),
        dataTestid: 'kpi-assets-managed',
        icon: 'ChartColumn',
        color: 'success',
      },
      {
        text: t('accounts.monthly-income', { ns: 'components' }),
        value: formatToMoney(0, currency),
        dataTestid: 'kpi-monthly-income',
        icon: 'Calendar',
        color: 'secondary',
      },
    ],
    [clientsPage, t, currency],
  );

  const onTextSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTextSearch(event.target.value);
  }, []);

  const [deleteCitecUserClientMutation] =
    advisoryApi.useDeleteCitecUserClientMutation();

  const onDeleteClientInternal = useCallback(
    async (client_id: number) => {
      try {
        await deleteCitecUserClientMutation({ client_id }).unwrap();

        enqueueSnackbar(t('accounts.client-deleted', { ns: 'components' }), {
          ...commonSnackProps,
        });
      } catch {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('accounts.error-deleting-client', { ns: 'components' }),
          content: t('accounts.error-request', { ns: 'components' }),
          buttons: {
            confirm: { show: true, text: t('common.ok', { ns: 'common' }) },
          },
        });
      }
    },
    [deleteCitecUserClientMutation, enqueueSnackbar, t],
  );

  const findClientRow = useCallback(
    (clientId: number) => clientsData.find(row => row.id === clientId),
    [clientsData],
  );

  const onDeleteClient = useCallback(
    (clientId: number) => {
      const row = findClientRow(clientId);

      if (!row) {
        console.error('Client not found for deletion');

        return;
      }

      showConfirm({
        title: t('accounts.delete-client-title', { ns: 'components' }),
        message: t('accounts.delete-client-message', {
          ns: 'components',
          clientName: row.name ? row.name : row.email,
        }),
        confirmText: t('common.delete', { ns: 'common' }),
        onConfirm: () => onDeleteClientInternal(clientId),
      });
    },
    [showConfirm, onDeleteClientInternal, t, findClientRow],
  );

  const [addCitecUserClientMutation] = advisoryApi.useAddCitecUserClientMutation();

  const onAddCitecUserClient = useCallback(async () => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).INVITE_CITEC_USER_CLIENT,
      title: t('accounts.invite-new-client', { ns: 'components' }),
      buttons: {
        cancel: {
          show: true,
          text: t('common.cancel', { ns: 'common' }),
        },
        confirm: {
          show: true,
          text: t('accounts.invite', { ns: 'components' }),
        },
      },
      preConfirm: async () => {
        const form = document.querySelector('#inviteClientForm') as HTMLFormElement;

        const citecUserData = buildCreateClientBody(new FormData(form));

        const result = await addCitecUserClientMutation(citecUserData).unwrap();

        return { data: result };
      },
    }).then(result => {
      const value = result.value as ExpectedResultValue | undefined;

      if (result.isConfirmed && value?.data) {
        enqueueSnackbar(t('accounts.client-invited', { ns: 'components' }), {
          ...commonSnackProps,
        });
      } else if (result.isConfirmed && value?.error) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: value.error.data
            ? t('common.error', { ns: 'common' })
            : t('accounts.cant-invite-client', { ns: 'components' }),
          content: value.error.data
            ? value.error.data.detail
            : t('accounts.error-inviting-client', { ns: 'components' }),
          buttons: {
            confirm: { show: true, text: t('common.ok', { ns: 'common' }) },
          },
        });
      }
    });
  }, [addCitecUserClientMutation, enqueueSnackbar, t]);

  const onSetClientProfile = useCallback(
    (clientId: number, shouldNavigate = true) => {
      const row = findClientRow(clientId);

      if (row) {
        options?.onSetClientProfile?.(row, shouldNavigate);
      }
    },
    [options, findClientRow],
  );

  const onViewProfile = useCallback(
    (clientId: number) => {
      const row = findClientRow(clientId);

      if (row) {
        options?.onViewProfile?.(row);
      }
    },
    [options, findClientRow],
  );

  const onSelectClient = useCallback(
    (client: ClientRow) => {
      options?.onSelectClient?.(client);
    },
    [options],
  );

  const pageCount = clientsPage?.pages ?? 0;

  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex: Math.max(page - 1, 0),
      pageSize: CLIENTS_PAGE_SIZE,
    }),
    [page],
  );

  const onPaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;

      setPage(next.pageIndex + 1);
    },
    [pagination],
  );

  return {
    clients: clientsData,
    totalClients: clientsPage?.total_clients ?? 0,
    isLoading,
    status: clientsStatus,
    hasData: clientsHasData,
    onRefresh: onRefreshClients,
    kpis,
    textSearch,
    onTextSearch,
    onDeleteClient,
    onAddCitecUserClient,
    onSetClientProfile,
    onViewProfile,
    onSelectClient,
    userId,
    pagination,
    pageCount,
    onPaginationChange,
  };
};
